import type {
  GameTypeData,
  GameTypeRequestData,
  GameTypesQueryParams
} from '@library/types/gameTypes';
import {
  mapGameTypeDataToRequest as mapGameTypeDataToRequestUtil,
  transformGameTypeData
} from '@bo-utils/gameTypes';
import { useCallback, useMemo } from 'react';
import {
  useCreateGameTypeMutation,
  useGetGameTypesQuery,
  useUpdateGameTypeMutation
} from '@bo-services/gameTypesApi';
import type { LeaguesList } from '@library/types/leagues';

export const useGameTypes = ({
  gameTypesQueryParams,
  currentItemId,
  setSubmitMessage
}: {
  gameTypesQueryParams: GameTypesQueryParams;
  leaguesList: LeaguesList;
  currentItemId: string;
  setSubmitMessage: (message: string) => void;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetGameTypesQuery(gameTypesQueryParams);

  const [createGameType] = useCreateGameTypeMutation();
  const [updateGameType] = useUpdateGameTypeMutation();

  const gameTypesData = useMemo(() => {
    return transformGameTypeData(response);
  }, [response]);

  const mapGameTypeDataToRequest = useCallback(
    (gameTypeFormData: Partial<GameTypeData>): GameTypeRequestData => {
      return mapGameTypeDataToRequestUtil(gameTypeFormData);
    },
    []
  );

  const handleUpdate = useCallback(
    async (formData: Partial<GameTypeData>) => {
      await updateGameType({
        id: currentItemId,
        data: mapGameTypeDataToRequest(formData)
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Game Type updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Game Type - ${error}`);
        });
    },
    [updateGameType, currentItemId, mapGameTypeDataToRequest, setSubmitMessage]
  );

  const handleCreate = useCallback(
    async (formData: Partial<GameTypeData>) => {
      await createGameType(mapGameTypeDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Game Type created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Game Type - ${error}`);
        });
    },
    [createGameType, mapGameTypeDataToRequest, setSubmitMessage]
  );

  return {
    createGameType: handleCreate,
    isError,
    isLoading,
    isSuccess,
    mapGameTypeDataToRequest,
    meta: response?.meta || {},
    page: response?.meta.current_page,
    gameTypes: gameTypesData || [],
    totalPages: response?.meta.total_pages || 0,
    updateGameType: handleUpdate
  };
};
